import { useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { MessageCircle, X, Send, Loader2, Trash2 } from 'lucide-react';
import { useAuthStore } from '@/stores/auth';
import { useChatStore } from '@/stores/chat';

const SUGGESTIONS_BY_ROLE: Record<string, string[]> = {
  Owner: [
    'How many stones are in polishing right now?',
    'Which polisher has the best yield this quarter?',
    'Show me recent exports and revenue.',
    'Are there any open critical alerts?',
  ],
  OperationsManager: [
    'What stones are overdue with polishers?',
    'Show me current stock summary.',
    'Where is stone 220005/2/1?',
    'What rejections happened recently?',
  ],
  SuperAdmin: [
    'Show pipeline overview.',
    'List open alerts by severity.',
    'How many stones in stock?',
    'Which workers polished the most this month?',
  ],
};

export default function ChatWidget() {
  const { open, messages, streaming, toggle, setOpen, pushUser, startAssistant, appendAssistant, finishAssistant, setError, reset } = useChatStore();
  const accessToken = useAuthStore((s) => s.accessToken);
  const role = useAuthStore((s) => s.user?.role) || 'Owner';
  const [input, setInput] = useState('');
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, streaming]);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  const sendMessage = async (text: string) => {
    if (!text.trim() || streaming) return;
    const history = messages.map((m) => ({ role: m.role, content: m.content }));
    pushUser(text);
    startAssistant();
    setInput('');

    try {
      const baseUrl = import.meta.env.VITE_API_URL || '';
      const res = await fetch(`${baseUrl}/api/chat`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ message: text, conversationHistory: history }),
      });

      if (!res.ok) {
        const err = await res.text().catch(() => '');
        setError(`Error ${res.status}: ${err.slice(0, 200) || 'request failed'}`);
        return;
      }
      if (!res.body) {
        setError('No response body from server.');
        return;
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = '';
      let toolsUsed: string[] | undefined;

      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const events = buf.split('\n\n');
        buf = events.pop() || '';
        for (const evt of events) {
          const line = evt.trim();
          if (!line.startsWith('data:')) continue;
          const payload = line.slice(5).trim();
          try {
            const json = JSON.parse(payload);
            if (json.token) appendAssistant(json.token);
            if (json.error) {
              setError(json.error);
              return;
            }
            if (json.done) {
              toolsUsed = json.toolsUsed;
            }
          } catch {
            // skip malformed
          }
        }
      }
      finishAssistant(toolsUsed);
    } catch (err: any) {
      setError(err?.message || 'Network error');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
    }
  };

  const suggestions = SUGGESTIONS_BY_ROLE[role] || SUGGESTIONS_BY_ROLE.Owner;

  return (
    <>
      {/* Floating button */}
      {!open && (
        <button
          onClick={toggle}
          className="fixed bottom-6 right-6 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-gold to-gold-light shadow-xl transition-transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-gold/40"
          aria-label="Open chat assistant"
          title="Aditi assistant"
        >
          <MessageCircle size={22} className="text-[#0a0a0b]" />
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div className="fixed bottom-6 right-6 z-50 flex h-[520px] w-[400px] max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-2xl border border-gold/20 bg-card shadow-2xl">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-border bg-gradient-to-r from-gold/10 to-transparent px-4 py-3">
            <div className="flex items-center gap-2">
              <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-gradient-to-br from-gold to-gold-light">
                <MessageCircle size={14} className="text-[#0a0a0b]" />
              </div>
              <div>
                <h3 className="text-sm font-bold text-gold">Aditi Assistant</h3>
                <p className="text-[10px] text-muted-foreground">gpt-oss:20b · live data</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              {messages.length > 0 && (
                <button
                  onClick={reset}
                  disabled={streaming}
                  className="rounded p-1.5 text-muted-foreground hover:bg-secondary hover:text-foreground disabled:opacity-50"
                  title="Clear conversation"
                >
                  <Trash2 size={14} />
                </button>
              )}
              <button
                onClick={() => setOpen(false)}
                className="rounded p-1.5 text-muted-foreground hover:bg-secondary hover:text-foreground"
                aria-label="Close chat"
              >
                <X size={16} />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto px-3 py-4">
            {messages.length === 0 ? (
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  Ask anything about stones, polishers, exports, or alerts.
                </p>
                <div className="space-y-1.5">
                  {suggestions.map((s) => (
                    <button
                      key={s}
                      onClick={() => sendMessage(s)}
                      className="block w-full rounded-lg border border-border bg-secondary/40 px-3 py-2 text-left text-xs text-foreground transition-colors hover:border-gold/40 hover:bg-secondary"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              messages.map((m, i) => (
                <div
                  key={i}
                  className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm ${
                      m.role === 'user'
                        ? 'bg-gradient-to-br from-gold to-gold-light text-[#0a0a0b]'
                        : 'border border-border bg-secondary/60 text-foreground'
                    }`}
                  >
                    {m.role === 'assistant' && m.pending && !m.content ? (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Loader2 size={14} className="animate-spin" />
                        <span className="text-xs">thinking...</span>
                      </div>
                    ) : m.role === 'assistant' ? (
                      <div className="prose prose-sm prose-invert max-w-none [&_p]:my-1 [&_table]:my-2 [&_table]:text-xs [&_th]:border [&_th]:border-border [&_th]:px-2 [&_th]:py-1 [&_td]:border [&_td]:border-border [&_td]:px-2 [&_td]:py-1 [&_ul]:my-1 [&_ul]:pl-4 [&_ol]:my-1 [&_ol]:pl-4 [&_code]:rounded [&_code]:bg-secondary [&_code]:px-1 [&_code]:text-xs">
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>
                          {m.content || ''}
                        </ReactMarkdown>
                        {m.toolsUsed && m.toolsUsed.length > 0 && (
                          <p className="mt-1 text-[10px] text-muted-foreground/60">
                            sources: {m.toolsUsed.join(', ')}
                          </p>
                        )}
                      </div>
                    ) : (
                      <span className="whitespace-pre-wrap">{m.content}</span>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Input */}
          <div className="border-t border-border bg-background/50 px-3 py-2">
            <div className="flex items-end gap-2">
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                rows={1}
                placeholder="Ask about stones, polishers, alerts..."
                disabled={streaming}
                className="max-h-24 flex-1 resize-none rounded-lg border border-border bg-secondary px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/60 focus:border-gold/40 focus:outline-none disabled:opacity-50"
              />
              <button
                onClick={() => sendMessage(input)}
                disabled={!input.trim() || streaming}
                className="flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-to-br from-gold to-gold-light text-[#0a0a0b] transition-transform hover:scale-105 disabled:opacity-40 disabled:hover:scale-100"
                title="Send"
              >
                {streaming ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
              </button>
            </div>
            <p className="mt-1 text-[10px] text-muted-foreground/60">
              First reply may take 20–60 seconds (CPU inference).
            </p>
          </div>
        </div>
      )}
    </>
  );
}
